/* eslint-disable no-unused-vars */
import React, { useState, useRef } from 'react';
import { MODEL_PLATFORMS } from '../hooks/useRevenueUpload';

const thStyle = { padding: '6px 10px', fontSize: 9, color: 'var(--t4)', textAlign: 'left',
  fontWeight: 400, letterSpacing: '.08em', textTransform: 'uppercase',
  background: 'var(--s1)', whiteSpace: 'nowrap' };

const OTHER_SUB_CATEGORIES = [
  { key: 'other_platform_deal',  label: 'Platform Deal' },
  { key: 'other_third_party',    label: 'Third Party Reseller' },
  { key: 'other_physical',       label: 'Physical' },
  { key: 'other_other',          label: 'Other' },
];

function PlatformMappingRow({ platform, mapping, onSave }) {
  // mapping may be a simple key like 'steam' or a composite like 'other:other_third_party'
  const [baseVal, setBaseVal]   = useState(mapping?.startsWith('other:') ? 'other' : (mapping || ''));
  const [subVal,  setSubVal]    = useState(mapping?.startsWith('other:') ? mapping.split(':')[1] : '');
  const currentSaved = mapping || '';
  const currentVal   = baseVal === 'other' && subVal ? `other:${subVal}` : baseVal;
  const isDirty      = currentVal !== currentSaved;

  return (
    <tr style={{ borderBottom: '1px solid var(--e0)' }}>
      <td style={{ padding: '5px 10px', fontSize: 11, color: 'var(--t1)' }}>{platform}</td>
      <td style={{ padding: '5px 8px' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <select value={baseVal} onChange={e => { setBaseVal(e.target.value); setSubVal(''); }}
            style={{ fontSize: 11, padding: '3px 20px 3px 6px', flex: 1 }}>
            <option value="">— unmapped —</option>
            {MODEL_PLATFORMS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            <option value="ignore">Ignore</option>
          </select>
          {baseVal === 'other' && (
            <select value={subVal} onChange={e => setSubVal(e.target.value)}
              style={{ fontSize: 11, padding: '3px 20px 3px 6px', flex: 1 }}>
              <option value="">— sub-category —</option>
              {OTHER_SUB_CATEGORIES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          )}
        </div>
      </td>
      <td style={{ padding: '5px 8px', textAlign: 'center' }}>
        {isDirty ? (
          <button onClick={() => onSave(platform, currentVal)}
            style={{ fontSize: 9, padding: '3px 10px', background: 'var(--blue-dim)',
              border: '1px solid var(--blue)', color: 'var(--blue)',
              borderRadius: 'var(--r)', cursor: 'pointer' }}>Save</button>
        ) : mapping ? (
          <span style={{ fontSize: 10, color: 'var(--green)' }}>✓</span>
        ) : null}
      </td>
    </tr>
  );
}

function TitleMappingRow({ title, mapping, onSave, games }) {
  const [val, setVal] = useState(mapping || '');
  const isDirty = val !== (mapping || '');
  return (
    <tr style={{ borderBottom: '1px solid var(--e0)' }}>
      <td style={{ padding: '5px 10px', fontSize: 11, color: 'var(--t1)' }}>{title}</td>
      <td style={{ padding: '5px 8px' }}>
        <select value={val} onChange={e => setVal(e.target.value)}
          style={{ fontSize: 11, padding: '3px 20px 3px 6px', width: '100%' }}>
          <option value="">— unmapped —</option>
          {(games || []).map(g => <option key={g} value={g}>{g}</option>)}
          <option value="ignore">Ignore</option>
        </select>
      </td>
      <td style={{ padding: '5px 8px', textAlign: 'center' }}>
        {isDirty ? (
          <button onClick={() => onSave(title, val)}
            style={{ fontSize: 9, padding: '3px 10px', background: 'var(--blue-dim)',
              border: '1px solid var(--blue)', color: 'var(--blue)',
              borderRadius: 'var(--r)', cursor: 'pointer' }}>Save</button>
        ) : mapping ? (
          <span style={{ fontSize: 10, color: 'var(--green)' }}>✓</span>
        ) : null}
      </td>
    </tr>
  );
}

export default function RevenueUploadPanel({
  uploads, sourcePlatforms, sourceTitles, platformMappings, titleMappings, loading,
  uploadRevenue, savePlatformMapping, saveTitleMapping, games,
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg('Parsing file…');
    try {
      const buf = await file.arrayBuffer();
      setUploadMsg('Uploading…');
      const result = await uploadRevenue(buf, file.name);
      setUploadMsg(`✓ Uploaded ${file.name} — ${result.rowCount} rows across ${result.months.length} months (${result.months[0]} → ${result.months[result.months.length - 1]})`);
    } catch (err) {
      setUploadMsg(`⚠ Error: ${err.message}`);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const pmMapped   = sourcePlatforms.filter(p => platformMappings[p] && platformMappings[p] !== 'ignore').length;
  const tmMapped   = sourceTitles.filter(t => titleMappings[t] && titleMappings[t] !== 'ignore').length;

  return (
    <div>
      {/* Upload */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <span className="card-title">Upload Revenue Data</span>
          <span className="card-meta">Excel · All Data tab · Col A=Month, B=Platform, E=Title, P=Revenue GBP</span>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFile}
              disabled={uploading} style={{ fontSize: 12, color: 'var(--t2)' }} />
          </div>
          {uploadMsg && (
            <div style={{ fontSize: 11, color: uploadMsg.startsWith('⚠') ? 'var(--red)' : 'var(--green)',
              padding: '6px 10px', background: 'var(--s1)', borderRadius: 'var(--r)',
              border: '1px solid var(--e0)', marginBottom: 8 }}>
              {uploadMsg}
            </div>
          )}
          <div style={{ fontSize: 10, color: 'var(--t4)' }}>
            Upload the revenue extract. Map platforms and titles to model items below.
            For any month ≤ month-end, net revenue for that game/platform will use these actuals.
          </div>
        </div>
      </div>

      {/* Upload history */}
      {uploads.length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-head"><span className="card-title">Upload History</span></div>
          <div className="card-body" style={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['File','Uploaded','Months','Rows'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {uploads.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--e0)' }}>
                    <td style={{ padding: '5px 10px', fontSize: 12, color: 'var(--t1)' }}>{u.filename}</td>
                    <td style={{ padding: '5px 10px', fontSize: 11, color: 'var(--t3)' }}>
                      {new Date(u.uploaded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '5px 10px', fontSize: 11, color: 'var(--t3)' }}>
                      {(u.months_covered || []).length} · {u.months_covered?.[0]} → {u.months_covered?.[u.months_covered.length - 1]}
                    </td>
                    <td style={{ padding: '5px 10px', fontSize: 11, color: 'var(--t3)' }}>{(u.row_count || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Platform mapping */}
      {sourcePlatforms.length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-head">
            <span className="card-title">Platform Mapping</span>
            <span className="card-meta">{pmMapped} of {sourcePlatforms.length} mapped</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Source Platform','Maps To',''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {sourcePlatforms.map(p => (
                  <PlatformMappingRow key={p} platform={p}
                    mapping={platformMappings[p]}
                    onSave={savePlatformMapping} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Title mapping */}
      {sourceTitles.length > 0 && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">Title Mapping</span>
            <span className="card-meta">{tmMapped} of {sourceTitles.length} mapped</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Source Title','Maps To',''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {sourceTitles.map(t => (
                  <TitleMappingRow key={t} title={t}
                    mapping={titleMappings[t]}
                    onSave={saveTitleMapping}
                    games={games} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sourcePlatforms.length === 0 && !loading && (
        <div className="nomsg">No revenue data yet — upload a file to get started.</div>
      )}
    </div>
  );
}
